<template>
  <div>
    <brb-input-group
      :prepend="label"
      :append="append"
      :required="required"
      :size="size"
    >
      <template v-if="['datetime', 'date'].includes(type)">
        <b-input
          :value="formattedDate"
          :id="`custom-date-picker-input-${uniqeId}`"
          style="flex-grow: 2.5 !important"
          :class="[
            'custom-date-time-picker-input',
            { 'text-danger': isSelectedDateFriday },
            { 'invalid-input': error }
          ]"
          readonly
        />

        <date-picker
          v-model="date"
          v-bind="$attrs"
          :custom-input="`#custom-date-picker-input-${uniqeId}`"
          :highlight="highlight"
          :min="minDate"
          :max="maxDate"
          display-format="dddd jYYYY/jMM/jDD"
          type="date"
          format="YYYY-MM-DD"
          :popover="popover"
          auto-submit
          @input="onDateChange"
        />
      </template>

      <template v-if="['datetime', 'time'].includes(type)">
        <b-input
          :value="formattedTime"
          :id="`custom-time-picker-input-${uniqeId}`"
          :class="['custom-date-time-picker-input', { 'invalid-input': error }]"
          readonly
        />

        <date-picker
          v-model="time"
          v-bind="$attrs"
          :custom-input="`#custom-time-picker-input-${uniqeId}`"
          :min="minTime"
          :max="maxTime"
          :popover="popover"
          type="time"
          format="HH:mm"
          display-format="HH:mm"
          auto-submit
          @input="onTimeChange"
        />
      </template>

      <brb-btn
        v-if="model && !required"
        class="position-absolute rounded-circle p-1"
        style="
          width: 18px;
          height: 18px;
          top: 0;
          bottom: 0;
          left: 7px;
          margin: auto 0;
          opacity: 0.7;
        "
        size="sm"
        @click="model = null"
      >
        <font-awesome-icon icon="times" size="xs" class="align-middle" />
      </brb-btn>
    </brb-input-group>

    <span
      v-if="!noError && error && typeof error === 'string'"
      class="d-block text-right text-danger small mt-2"
    >
      {{ error }}
    </span>
  </div>
</template>

<script>
import DatePicker from 'vue-persian-datetime-picker'
import jMoment from 'moment-jalaali'
import moment from 'moment-timezone'

export default {
  name: 'BrbDateTimePickerNew',
  components: {
    DatePicker,
    BrbInputGroup: () => import('./BrbInputGroup.vue')
  },
  props: {
    value: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'datetime'
    },
    error: {
      type: [Boolean, String]
    },
    popover: {
      type: String,
      required: false,
      default: 'bottom'
    },
    noError: Boolean,
    minDate: String,
    maxDate: String,
    minTime: String,
    maxTime: String,
    endOfDay: Boolean,
    label: String,
    append: String,
    size: String,
    required: Boolean
  },
  data() {
    return {
      date: null,
      time: null
    }
  },
  computed: {
    uniqeId() {
      return Math.floor(Math.random() * 9999 + 1)
    },
    model: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    formattedDate() {
      if (this.model) {
        const date = moment(this.date, 'YYYY-MM-DD').tz(
          this.$config.timeZone.text
        )
        return jMoment(date).locale('fa').format('dddd jYYYY/jMM/jDD')
      }

      return null
    },
    formattedTime() {
      if (this.model) {
        return moment(this.time, 'HH:mm:ss')
          .tz(this.$config.timeZone.text)
          .format('HH:mm')
      }

      return null
    },
    isSelectedDateFriday() {
      if (['date', 'datetime'].includes(this.type) && this.model) {
        const date = moment(this.date, 'YYYY-MM-DD').tz(
          this.$config.timeZone.text
        )

        return date.day() === 5
      }

      return false
    }
  },
  watch: {
    value: {
      handler() {
        this.date = this.parseDate(this.value)
        this.time = this.parseTime(this.value)
      },
      immediate: true
    }
  },
  methods: {
    parseDate(value) {
      if (value) {
        return moment
          .utc(value, 'YYYY-MM-DDTHH:mm:ss.SSS')
          .tz(this.$config.timeZone.text)
          .format('YYYY-MM-DD')
      }

      return null
    },
    parseTime(value) {
      if (value) {
        if (
          moment(value, 'HH:mm:ss', true).isValid() ||
          moment(value, 'HH:mm', true).isValid()
        )
          return value
        return moment
          .utc(value, 'YYYY-MM-DDTHH:mm:ss.SSS')
          .tz(this.$config.timeZone.text)
          .format('HH:mm:ss')
      }

      return null
    },
    onDateChange() {
      if (this.type === 'datetime' && !this.time) {
        if (this.endOfDay) {
          this.time = '23:59:59'
        } else {
          this.time = '00:00:00'
        }
      }

      this.changeModel()
    },
    onTimeChange() {
      this.changeModel()
    },
    changeModel() {
      switch (this.type) {
        case 'date':
          this.model = this.date
          break
        case 'time':
          this.model = this.time
          break
        case 'datetime':
          const format = 'YYYY-MM-DD HH:mm:ss'

          const m = moment(`${this.date} ${this.time}`, format)

          this.model = m.utc().toISOString().replace('.000Z', 'Z')
          break
      }
    },
    highlight(formatted, dateMoment, checkingFor) {
      if (checkingFor === 'day' && dateMoment.day() === 5) {
        return { class: 'friday-highlighted' }
      }
      return {}
    }
  }
}
</script>

<style lang="scss">
.custom-date-time-picker-input {
  background: transparent !important;
}

.vpd-wrapper.vpd-is-popover {
  z-index: 9999 !important;
}

.friday-highlighted {
  color: #fd6363;
  .vpd-day-effect {
    background-color: #fd6363 !important;
  }
  // for simple mode:
  &.vpd-addon-list-item {
    color: #fd6363 !important;
    &.vpd-selected {
      background-color: #f1d4de;
    }
  }
}
</style>
